<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

// Renders the ad/post a lead came from — captured once, at conversation creation,
// from the WhatsApp Cloud webhook's `referral` object (Click-to-WhatsApp /
// Click-to-Instagram ads). See Whatsapp::IncomingMessageServiceHelpers#referral_conversation_attributes
// for the raw fields and MetaAds::ResolveAdDetailsJob for campaign_name/adset_name/ad_name
// (resolved async, may arrive a few seconds after the card first renders).
// Used in both the Kanban contact lightbox and the conversation info panel.
// Collapsed by default — the header alone ("Origem do lead · Anúncio") is always
// visible when there's origin data; click it to reveal the full card.
const props = defineProps({
  additionalAttributes: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();

const attrs = computed(() => props.additionalAttributes || {});
const hasOrigin = computed(
  () => Boolean(attrs.value.source_id) || Boolean(attrs.value.campaign_name)
);

const expanded = ref(false);

const isVideo = computed(() => attrs.value.media_type === 'video');
const sourceTypeLabel = computed(() => {
  const map = {
    ad: t('LEAD_ORIGIN.SOURCE_TYPE.AD'),
    post: t('LEAD_ORIGIN.SOURCE_TYPE.POST'),
  };
  return map[attrs.value.source_type] || attrs.value.source_type;
});

const bodyExpanded = ref(false);
const BODY_PREVIEW_LENGTH = 160;
const bodyIsLong = computed(
  () => (attrs.value.body || '').length > BODY_PREVIEW_LENGTH
);
const bodyText = computed(() => {
  if (!attrs.value.body) return '';
  if (bodyExpanded.value || !bodyIsLong.value) return attrs.value.body;
  return `${attrs.value.body.slice(0, BODY_PREVIEW_LENGTH)}…`;
});
</script>

<template>
  <div
    v-if="hasOrigin"
    class="flex flex-col border rounded-xl border-n-weak bg-n-solid-1"
  >
    <button
      type="button"
      class="flex items-center justify-between w-full gap-2 px-3 py-2 text-left"
      :aria-expanded="expanded"
      @click="expanded = !expanded"
    >
      <span
        class="inline-flex items-center gap-1.5 text-xs font-medium text-n-slate-11"
      >
        <span class="i-lucide-megaphone size-3.5" />
        {{ t('LEAD_ORIGIN.TITLE') }}
        <span
          v-if="sourceTypeLabel"
          class="px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide bg-n-alpha-2 text-n-slate-11"
        >
          {{ sourceTypeLabel }}
        </span>
      </span>
      <span
        class="shrink-0 text-n-slate-10 size-3.5"
        :class="expanded ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
      />
    </button>

    <div v-if="expanded" class="flex flex-col gap-3 px-3 pb-3">
      <div class="flex gap-3">
        <div
          v-if="attrs.thumbnail_url"
          class="relative shrink-0 overflow-hidden rounded-lg size-16 bg-n-alpha-2"
        >
          <img
            :src="attrs.thumbnail_url"
            alt=""
            class="object-cover size-full"
          />
          <span
            v-if="isVideo"
            class="absolute inset-0 flex items-center justify-center bg-black/30"
          >
            <span class="text-white i-lucide-play size-5" />
          </span>
        </div>

        <div class="flex flex-col min-w-0 gap-1">
          <p
            v-if="attrs.campaign_name || attrs.ad_name"
            class="text-xs font-semibold truncate text-n-slate-12"
          >
            {{ attrs.campaign_name }}
            <span v-if="attrs.campaign_name && attrs.ad_name" class="text-n-slate-10">
              ·
            </span>
            {{ attrs.ad_name }}
          </p>
          <p v-if="attrs.headline" class="text-sm font-medium text-n-slate-12">
            {{ attrs.headline }}
          </p>
          <p v-if="bodyText" class="text-xs whitespace-pre-line text-n-slate-11">
            {{ bodyText }}
            <button
              v-if="bodyIsLong"
              type="button"
              class="ml-1 font-medium text-n-blue-11 hover:underline"
              @click="bodyExpanded = !bodyExpanded"
            >
              {{
                bodyExpanded
                  ? t('LEAD_ORIGIN.SHOW_LESS')
                  : t('LEAD_ORIGIN.SHOW_MORE')
              }}
            </button>
          </p>
        </div>
      </div>

      <p
        v-if="attrs.welcome_message"
        class="px-2 py-1.5 text-xs italic border-l-2 rounded text-n-slate-11 border-n-slate-6 bg-n-alpha-1"
      >
        {{ attrs.welcome_message }}
      </p>

      <a
        v-if="attrs.source_url"
        :href="attrs.source_url"
        target="_blank"
        rel="noopener noreferrer nofollow"
        class="inline-flex items-center gap-1 text-xs font-medium w-fit text-n-blue-11 hover:underline"
      >
        <span class="i-lucide-external-link size-3" />
        {{ t('LEAD_ORIGIN.VIEW_SOURCE') }}
      </a>
    </div>
  </div>
</template>
